<template>
  <ValidationObserver ref="observer" v-slot="{ invalid }">
    <v-dialog v-model="dialogComputed" persistent scrollable max-width="800px">
      <v-card>
        <v-card-title class="primary white--text py-2" primary-title>
          {{ $t('ELE0188') }}
          <v-spacer />
          <Btn icon="mdi-close" @click="close" depressed iconProp color="white" />
        </v-card-title>
        <v-card-text>
          <v-container fluid class="px-1">
            <v-row>
              <v-col>
                <PartCodeInput v-model="partCd" disabled />
              </v-col>
              <v-col>
                <CompanyInput :label="$t('ELE0134')" v-model="manufacturerCd" disabled />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <XrfGroupInput v-model="xrfGroupCd" disabled />
              </v-col>
              <v-col>
                <TextFieldInputWithDatePicker
                  :label="$t('ELE0138')"
                  v-model="xrfInspDate"
                  disableFutureDate
                  @input="calcExpiredDate"
                  required
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextFieldRef :label="$t('ELE0530')" v-model="xrfInspCycle" right />
              </v-col>
              <v-col>
                <TextFieldInputWithDatePicker
                  :label="$t('ELE0254')"
                  v-model="xrfExpiredDate"
                  :disabled="isRequiredXrf"
                  :required="xrfInspCycle != ''"
                  :minDate="xrfInspDate"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextAreaInput :label="$t('ELE0054')" v-model="remarks" :maxLength="1000" rows="1" />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <XrfInspResultCopyToInput
                  v-model="inspResultCopyToList"
                  :ignorePartCd="partCd"
                  :manufacturerCd="manufacturerCd"
                  :xrfGroupCd="xrfGroupCd"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <span class="d-flex flex-row space-between">
            <BtnSave @click="onClickSave" :disabled="invalid" />
          </span>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </ValidationObserver>
</template>

<script>
import dayjs from 'dayjs'
import { API } from '@/plugins/api'
import { ValidationObserver } from 'vee-validate'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtils from '@/mixin/date-utils.js'

import SelectMultipleInput from '@/components/atoms/base/select/SelectMultipleInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import TextAreaInput from '@/components/atoms/base/text-field/TextAreaInput'
import Btn from '@/components/atoms/base/button/Btn'
import BtnSave from '@/components/atoms/button/BtnSave'
import BtnChooseValid from '@/components/atoms/button/BtnChooseValid'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'

import PartCodeInput from '@/components/atoms/select/PartCodeInput'
import CompanyInput from '@/components/atoms/select/CompanyInput'
import PartSupplierInput from '@/components/atoms/select/PartSupplierInput'
import XrfGroupInput from '@/components/atoms/select/XrfGroupInput'
import XrfInspResultCopyToInput from '@/components/atoms/select/XrfInspResultCopyToInput'

export default {
  name: 'XrfInspResultDialog',

  mixins: [APIUtils, Message, Loading, DateUtils],

  components: {
    ValidationObserver,
    SelectMultipleInput,
    TextFieldRef,
    TextAreaInput,
    Btn,
    BtnSave,
    BtnChooseValid,
    Checkbox,
    TextFieldInputWithDatePicker,
    PartCodeInput,
    CompanyInput,
    PartSupplierInput,
    XrfGroupInput,
    XrfInspResultCopyToInput,
  },

  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    partCd: {
      type: String,
      required: true,
    },
    manufacturerCd: {
      type: String,
      required: true,
    },
    xrfGroupCd: {
      type: String,
      required: true,
    },
    xrfInspCycle: {
      type: String,
      required: true,
    },
  },

  watch: {
    dialog() {
      this.calcExpiredDate(this.xrfInspDate)
    },
  },

  data() {
    return {
      xrfInspDate: dayjs().format('YYYY-MM-DD'),
      xrfExpiredDate: '',
      remarks: '',
      inspResultCopyToList: [],
    }
  },

  computed: {
    isRequiredXrf: {
      get() {
        return this.xrfInspCycle == ''
      },
    },
    dialogComputed: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
  },

  methods: {
    close() {
      this.dialogComputed = false
      Object.assign(this.$data, this.$options.data.call(this))
    },
    calcExpiredDate(value) {
      if (!this.isRequiredXrf) {
        this.xrfExpiredDate = dayjs(value).add(this.xrfInspCycle, 'd').format('YYYY-MM-DD')
      }
    },
    async onClickSave() {
      try {
        this.startLoading()
        const body = {
          partCd: this.partCd,
          manufacturerCd: this.manufacturerCd,
          xrfInspDate: this.xrfInspDate,
          xrfExpiredDate: this.isRequiredXrf ? null : this.xrfExpiredDate,
          remarks: this.remarks,
          inspResultCopyToList: this.inspResultCopyToList,
        }
        await API.post('/xrfs/register', body)

        this.setSuccessMessage(this.$t('MSG0011', { p0: this.$t('ELE0188') }))
        this.close()
        this.$emit('change')
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0188') })))
      } finally {
        this.stopLoading()
      }
    },
  },
  mounted() {
    this.calcExpiredDate(this.xrfInspDate)
  },
}
</script>
